import { createContext, useContext } from 'react';

/**
 * Per-user, per-view set of "hearted" (favourite) filter fields — the filters the chip bar shows by
 * default, even when they are empty. Persisted on the backend
 * (`/api/v1.0/users/table-filter-favourites`), so a user's pinned filters follow them between
 * devices and browsers.
 *
 * Three states, and they are all distinct:
 *   • no record for a view  → the bar follows the FE-computed defaults (`defaultShown` / first 7)
 *   • a non-empty list      → the user's own choice wins over those defaults
 *   • an *empty* list       → the user deliberately unpinned everything; only active filters show
 * That is why "no record" is `undefined` here and never an empty array.
 */
export interface TableFilterFavouritesContextValue {
  /** True once the bootstrap call has answered — successfully or not. */
  loaded: boolean;
  /** The stored favourites for a view, or `undefined` when the user never customised it. */
  favouritesFor: (viewKey: string) => string[] | undefined;
  /** Replaces a view's favourites (optimistic locally, debounced PUT to the backend). */
  setFavourites: (viewKey: string, filterKeys: string[]) => void;
  /**
   * Loads the whole map, once per session. Called by consumers rather than by the provider itself:
   * the provider wraps the app, but a user who never opens a filtered table should not pay for it.
   */
  ensureLoaded: () => void;
}

export const TableFilterFavouritesContext = createContext<TableFilterFavouritesContextValue>({
  loaded: false,
  favouritesFor: () => undefined,
  setFavourites: () => undefined,
  ensureLoaded: () => undefined,
});

/**
 * `filterName` is FE-owned and free-form (`expedition_list`, `timeslot-calendar`,
 * `audit_log_${entityType}`, …); the backend stores it as an opaque key. Normalised to lowercase
 * snake_case so a view that differs only in case or separator can't end up with two records.
 */
export const toViewKey = (filterName: string): string => filterName.toLowerCase().replace(/[^a-z0-9_]/g, '_');

/**
 * The only supported way to read favourites. Outside the provider it answers "nothing stored", so a
 * filter bar rendered in isolation still shows the FE defaults instead of breaking.
 */
export const useTableFilterFavourites = (): TableFilterFavouritesContextValue => useContext(TableFilterFavouritesContext);
